import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { supabase } from "./lib/supabase";
import COLORS from "./config/colors";

async function generateDrawing(mode, project) {
  if (!supabase) return null;
  const { data, error } = await supabase.functions.invoke("generate-drawing", {
    body: { mode, project },
  });
  if (error) { console.error("generate-drawing error:", error); return null; }
  return data;
}

async function chatWithDrawingContext(messages, project, currentDrawings) {
  if (!supabase) return null;
  const { data, error } = await supabase.functions.invoke("owner-chat", {
    body: {
      messages: messages.map(m => ({
        role: m.role,
        text: m.role === "user" && m.image
          ? `[User uploaded a site measurement photo]\n\n${m.text}`
          : m.text,
      })),
      drawingContext: {
        project,
        drawingCount: currentDrawings.length,
        lastDrawingNotes: currentDrawings[currentDrawings.length - 1]?.notes || null,
      },
    },
  });
  if (error) { console.error("drawing-chat error:", error); return null; }
  return data?.response || null;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("File read failed"));
    reader.readAsDataURL(file);
  });
}

export default function ProjectVisualizer({ customer }) {
  const [activeTab, setActiveTab] = useState("drawings");
  const [drawings, setDrawings] = useState([]);
  const [renderings, setRenderings] = useState([]);
  const [generating, setGenerating] = useState(null);

  // AI Drawing Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Project specs — editable by Chris
  const [specs, setSpecs] = useState({
    width: 0, depth: 0, height: 36, // deck height in inches
    joistSpacing: 12, // 12" OC for snow load
    joistSize: "2x10", beamSize: "3-ply 2x10", postSize: "6x6",
    footingDiameter: 18, footingDepth: 24,
    snowLoad: 200, // psf
    railing: "cable", railingHeight: 42, // inches
  });

  // Init specs from customer data
  useEffect(() => {
    if (customer) {
      const sqft = customer.sqft || 400;
      setSpecs(prev => ({
        ...prev,
        width: Math.round(Math.sqrt(sqft) * 1.25),
        depth: Math.round(Math.sqrt(sqft) * 0.8),
      }));
      setChatMessages([{
        role: "assistant",
        text: `Ready to work on the **${customer.name}** project — ${customer.sqft || "?"}sqft ${customer.material || "deck"}. I can help you:\n\n- Refine dimensions and structural members\n- Specify joist spacing, beam sizes, post locations\n- Review connection hardware (hangers, brackets)\n- Adjust for snow loads based on the address\n- Incorporate your site measurements\n\nUpload a measurement photo or tell me what you need to change.`,
      }]);
    }
  }, [customer]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const project = useMemo(() => ({
    customerName: customer?.name || "Customer",
    sqft: specs.width * specs.depth,
    width: specs.width,
    depth: specs.depth,
    material: customer?.material || "Composite decking",
    railing: specs.railing,
    extras: [],
    notes: customer?.notes || "",
    // Include structural specs for the AI
    specs: { ...specs },
  }), [customer?.name, customer?.material, customer?.notes, specs]);

  const generate = async (mode) => {
    setGenerating(mode);
    const result = await generateDrawing(mode, project);
    if (result?.success) {
      if (result.type === "drawing") {
        setDrawings(prev => [...prev, result]);
      } else {
        setRenderings(prev => [...prev, result]);
      }
    }
    setGenerating(null);
  };

  const sendChat = useCallback(async (text, imageData) => {
    if (!text?.trim() && !imageData) return;
    const userMsg = { role: "user", text: text?.trim() || "Uploaded site photo — please review.", image: imageData || null };
    const updated = [...chatMessages, userMsg];
    setChatMessages(updated);
    setChatInput("");
    setChatLoading(true);

    const response = await chatWithDrawingContext(updated, project, drawings);

    if (response) {
      setChatMessages(prev => [...prev, { role: "assistant", text: response }]);

      // Check if the AI suggested spec changes — parse for dimensions
      const widthMatch = response.match(/width[:\s]+(\d+)/i);
      const depthMatch = response.match(/depth[:\s]+(\d+)/i);
      const joistMatch = response.match(/(\d+)[""]?\s*[oO]\.?[cC]/);
      const snowMatch = response.match(/(\d+)\s*psf/);
      if (widthMatch) setSpecs(prev => ({ ...prev, width: parseInt(widthMatch[1]) }));
      if (depthMatch) setSpecs(prev => ({ ...prev, depth: parseInt(depthMatch[1]) }));
      if (joistMatch) setSpecs(prev => ({ ...prev, joistSpacing: parseInt(joistMatch[1]) }));
      if (snowMatch) setSpecs(prev => ({ ...prev, snowLoad: parseInt(snowMatch[1]) }));
    } else {
      setChatMessages(prev => [...prev, { role: "assistant", text: "Couldn't reach the AI. Try again." }]);
    }
    setChatLoading(false);
  }, [chatMessages, project, drawings]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await fileToBase64(file);
    sendChat(`Here are my site measurements from this photo. Please review and suggest any structural changes.`, base64);
    e.target.value = "";
  };

  const styles = {
    container: { fontFamily: "'DM Sans', sans-serif" },
    tabs: { display: "flex", gap: 4, marginBottom: 20, borderBottom: "2px solid rgba(155,142,130,0.12)" },
    tab: (active) => ({ padding: "10px 18px", border: "none", borderBottom: `3px solid ${active ? COLORS.honey : "transparent"}`, background: "none", color: active ? COLORS.bark : COLORS.stone, fontWeight: active ? 700 : 500, fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", marginBottom: -2 }),
    card: { background: COLORS.white, borderRadius: 14, padding: 20, boxShadow: "0 1px 4px rgba(44,24,16,0.06)", border: "1px solid rgba(155,142,130,0.12)", marginBottom: 16 },
    btn: (color) => ({ padding: "10px 18px", borderRadius: 10, border: "none", background: color || COLORS.honey, color: COLORS.white, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }),
    label: { fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: COLORS.stone, marginBottom: 6 },
    sectionTitle: { fontSize: 20, fontWeight: 700, fontFamily: "'Playfair Display', serif", color: COLORS.bark, marginBottom: 4 },
    specInput: { width: "100%", padding: "6px 10px", borderRadius: 6, border: "1px solid rgba(155,142,130,0.3)", fontSize: 13, fontFamily: "'DM Sans', sans-serif", background: COLORS.cream, boxSizing: "border-box" },
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.sectionTitle}>Project Visualizer</h2>
      <p style={{ color: COLORS.stone, fontSize: 13, marginBottom: 16 }}>
        {customer ? `${customer.name} — ${specs.width}'×${specs.depth}' (${specs.width * specs.depth}sqft) ${customer.material || "deck"}` : "Select a customer from the CRM to generate drawings"}
      </p>

      <div style={styles.tabs}>
        <button onClick={() => setActiveTab("drawings")} style={styles.tab(activeTab === "drawings")}>Construction Drawings</button>
        <button onClick={() => setActiveTab("renderings")} style={styles.tab(activeTab === "renderings")}>3D Renderings</button>
      </div>

      {/* Main layout: drawings left, chat + specs right */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 20, alignItems: "start" }}>
        {/* LEFT: Drawings / Renderings */}
        <div>
          {activeTab === "drawings" && (
            <>
              <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                {[
                  { mode: "plan", label: "Plan View" },
                  { mode: "elevation", label: "Elevation" },
                  { mode: "framing", label: "Framing Detail" },
                ].map(d => (
                  <button key={d.mode} onClick={() => generate(d.mode)} disabled={generating === d.mode || !customer}
                    style={{ ...styles.btn(generating === d.mode ? COLORS.stone : COLORS.honey), opacity: generating === d.mode ? 0.6 : 1 }}>
                    {generating === d.mode ? "Generating..." : d.label}
                  </button>
                ))}
              </div>

              {drawings.length === 0 && !generating && (
                <div style={{ ...styles.card, textAlign: "center", padding: 40, color: COLORS.stone }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>📐</div>
                  <p>Generate a drawing above, then refine it with the AI chat.</p>
                </div>
              )}

              {drawings.map((d, i) => (
                <div key={i} style={styles.card}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: COLORS.bark }}>{d.title}</div>
                      <div style={{ fontSize: 11, color: COLORS.red, fontWeight: 600 }}>PRELIMINARY — VERIFY ON SITE</div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => { generate(drawings.indexOf(d) >= 0 ? "plan" : "plan"); }}
                        style={{ ...styles.btn(COLORS.honey), padding: "6px 12px", fontSize: 11 }}>Regenerate</button>
                      <button
                        onClick={() => {
                          const blob = new Blob([d.svg], { type: "image/svg+xml" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a"); a.href = url;
                          a.download = `${d.title.replace(/\s+/g, '_')}.svg`; a.click();
                          URL.revokeObjectURL(url);
                        }}
                        style={{ ...styles.btn(COLORS.pine), padding: "6px 12px", fontSize: 11 }}>Download SVG</button>
                    </div>
                  </div>
                  <div style={{ background: COLORS.white, border: "1px solid rgba(155,142,130,0.15)", borderRadius: 8, overflow: "auto", padding: 12 }}
                    dangerouslySetInnerHTML={{ __html: d.svg }} />
                  {d.notes && <div style={{ marginTop: 10, fontSize: 12, color: COLORS.stone, lineHeight: 1.5 }}>{d.notes}</div>}
                </div>
              ))}
            </>
          )}

          {activeTab === "renderings" && (
            <>
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                {[
                  { mode: "rendering_overview", label: "Overview" },
                  { mode: "rendering_detail", label: "Detail" },
                  { mode: "rendering_lifestyle", label: "Lifestyle" },
                ].map(r => (
                  <button key={r.mode} onClick={() => generate(r.mode)} disabled={generating === r.mode || !customer}
                    style={{ ...styles.btn(generating === r.mode ? COLORS.stone : COLORS.blue), opacity: generating === r.mode ? 0.6 : 1 }}>
                    {generating === r.mode ? "Generating..." : r.label}
                  </button>
                ))}
              </div>

              {renderings.length === 0 && !generating && (
                <div style={{ ...styles.card, textAlign: "center", padding: 40, color: COLORS.stone }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>🏔️</div>
                  <p>Generate rendering descriptions for the customer.</p>
                </div>
              )}

              {renderings.map((r, i) => (
                <div key={i} style={styles.card}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: COLORS.bark, marginBottom: 8 }}>{r.title}</div>
                  <div style={{ padding: 12, background: COLORS.cream, borderRadius: 8, fontSize: 13, color: COLORS.slate, lineHeight: 1.6 }}>
                    {r.prompt}
                  </div>
                  {r.notes && <div style={{ marginTop: 8, fontSize: 12, color: COLORS.stone }}>{r.notes}</div>}
                </div>
              ))}
            </>
          )}
        </div>

        {/* RIGHT: Specs Panel + AI Chat */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, position: "sticky", top: 88 }}>
          {/* Structural Specs */}
          <div style={{ ...styles.card, padding: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.bark, marginBottom: 12 }}>Structural Specs</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12 }}>
              <div>
                <div style={styles.label}>Width (ft)</div>
                <input type="number" value={specs.width} onChange={e => setSpecs(s => ({ ...s, width: Number(e.target.value) }))} style={styles.specInput} />
              </div>
              <div>
                <div style={styles.label}>Depth (ft)</div>
                <input type="number" value={specs.depth} onChange={e => setSpecs(s => ({ ...s, depth: Number(e.target.value) }))} style={styles.specInput} />
              </div>
              <div>
                <div style={styles.label}>Joist Spacing</div>
                <select value={specs.joistSpacing} onChange={e => setSpecs(s => ({ ...s, joistSpacing: Number(e.target.value) }))} style={styles.specInput}>
                  <option value={12}>12" OC</option>
                  <option value={16}>16" OC</option>
                </select>
              </div>
              <div>
                <div style={styles.label}>Joist Size</div>
                <select value={specs.joistSize} onChange={e => setSpecs(s => ({ ...s, joistSize: e.target.value }))} style={styles.specInput}>
                  <option>2x8</option><option>2x10</option><option>2x12</option>
                </select>
              </div>
              <div>
                <div style={styles.label}>Beam</div>
                <select value={specs.beamSize} onChange={e => setSpecs(s => ({ ...s, beamSize: e.target.value }))} style={styles.specInput}>
                  <option>2-ply 2x10</option><option>3-ply 2x10</option><option>2-ply 2x12</option><option>3-ply 2x12</option><option>6x10</option>
                </select>
              </div>
              <div>
                <div style={styles.label}>Posts</div>
                <select value={specs.postSize} onChange={e => setSpecs(s => ({ ...s, postSize: e.target.value }))} style={styles.specInput}>
                  <option>4x4</option><option>6x6</option><option>4x6</option>
                </select>
              </div>
              <div>
                <div style={styles.label}>Snow Load (psf)</div>
                <input type="number" value={specs.snowLoad} onChange={e => setSpecs(s => ({ ...s, snowLoad: Number(e.target.value) }))} style={styles.specInput} />
              </div>
              <div>
                <div style={styles.label}>Footing Depth (in)</div>
                <input type="number" value={specs.footingDepth} onChange={e => setSpecs(s => ({ ...s, footingDepth: Number(e.target.value) }))} style={styles.specInput} />
              </div>
            </div>
            <div style={{ marginTop: 8, padding: 8, background: COLORS.cream, borderRadius: 6, fontSize: 11, color: COLORS.stone, textAlign: "center" }}>
              {specs.width * specs.depth} sqft · {specs.joistSize} @ {specs.joistSpacing}" OC · {specs.beamSize} beam · {specs.postSize} posts · {specs.snowLoad} psf
            </div>
          </div>

          {/* AI Drawing Chat */}
          <div style={{ ...styles.card, padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", height: 380 }}>
            <div style={{ padding: "12px 16px", background: COLORS.bark, color: COLORS.snow, fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 18 }}>🧠</span>
              <div>
                <div>Drawing Assistant</div>
                <div style={{ fontSize: 10, opacity: 0.6, fontWeight: 400 }}>Refine specs, upload measurements, adjust structure</div>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
              {chatMessages.map((msg, i) => (
                <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
                  <div style={{
                    maxWidth: "88%", padding: "10px 14px", borderRadius: 12, fontSize: 12.5, lineHeight: 1.5,
                    background: msg.role === "user" ? COLORS.bark : COLORS.cream,
                    color: msg.role === "user" ? COLORS.snow : COLORS.bark,
                    borderBottomRightRadius: msg.role === "user" ? 4 : 12,
                    borderBottomLeftRadius: msg.role === "user" ? 12 : 4,
                  }}>
                    {msg.image && <div style={{ fontSize: 10, color: COLORS.honey, marginBottom: 4 }}>📷 Photo attached</div>}
                    {msg.text.split('\n').map((line, j) => {
                      if (line.startsWith('**') && line.endsWith('**')) return <div key={j} style={{ fontWeight: 700 }}>{line.slice(2, -2)}</div>;
                      if (line.startsWith('- ')) return <div key={j} style={{ paddingLeft: 8 }}>• {line.slice(2)}</div>;
                      return <div key={j}>{line || <br />}</div>;
                    })}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div style={{ display: "flex", gap: 4, padding: "8px 14px" }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.stone, opacity: 0.4, animation: `pulse 1s ease-in-out ${i * 0.15}s infinite` }} />
                  ))}
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Quick actions */}
            {chatMessages.length <= 2 && (
              <div style={{ display: "flex", gap: 4, padding: "6px 12px", flexWrap: "wrap" }}>
                {[
                  "What hardware do I need?",
                  "Check my beam span",
                  "Footing requirements",
                  "Review snow load design",
                ].map(q => (
                  <button key={q} onClick={() => sendChat(q)} disabled={chatLoading}
                    style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid rgba(196,145,59,0.3)`, background: "rgba(196,145,59,0.06)", color: COLORS.bark, fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div style={{ padding: 10, borderTop: "1px solid rgba(155,142,130,0.12)", display: "flex", gap: 6 }}>
              <button onClick={() => fileInputRef.current?.click()} title="Upload measurement photo"
                style={{ width: 36, height: 36, borderRadius: 8, border: "1px solid rgba(155,142,130,0.3)", background: COLORS.cream, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                📷
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: "none" }} />
              <input value={chatInput} onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && !chatLoading && sendChat(chatInput)}
                placeholder="Change to 22x18... Add Simpson hangers..."
                disabled={chatLoading}
                style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(155,142,130,0.3)", fontSize: 12, fontFamily: "'DM Sans', sans-serif", outline: "none", background: COLORS.cream }} />
              <button onClick={() => sendChat(chatInput)} disabled={chatLoading || !chatInput.trim()}
                style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: chatLoading ? COLORS.stone : COLORS.bark, color: COLORS.honey, cursor: chatLoading ? "wait" : "pointer", fontSize: 14, flexShrink: 0 }}>
                →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
